import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { Bell, CheckCircle } from "@phosphor-icons/react";

// In-app notifications bell. Polls every 60s for unread count, opens a dropdown
// with recent notifications, allows mark-as-read + navigate to task.
// On mount, fires a fire-and-forget POST /notifications/check-overdue (throttled server-side).

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // fail silently — bell just shows last known count
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    // Kick off overdue check + initial fetch on mount
    api.post("/notifications/check-overdue").catch(() => {}).finally(refreshCount);
    // Poll unread count every 60s
    const t = setInterval(refreshCount, 60000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((cur) => cur.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post(`/notifications/read-all`);
      const now = new Date().toISOString();
      setNotifications((cur) => cur.map((n) => n.read_at ? n : { ...n, read_at: now }));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const openTask = (n) => {
    if (!n.read_at) markRead(n.id);
    if (n.task_id) {
      const params = new URLSearchParams();
      if (n.project_id) params.set("project_id", n.project_id);
      params.set("focus", n.task_id);
      navigate(`/?${params.toString()}`);
    }
    setOpen(false);
  };

  const relTime = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toISOString().slice(0, 10);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="notification-bell-wrapper">
      <button
        type="button"
        onClick={toggle}
        className="relative h-9 w-9 flex items-center justify-center rounded-md text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell size={18} weight={unreadCount > 0 ? "fill" : "regular"} />
        {unreadCount > 0 ? (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] leading-none font-bold text-white bg-red-500 flex items-center justify-center"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 mt-2 w-96 bg-white border border-neutral-200 rounded-lg shadow-xl z-50 max-h-[70vh] flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Notifications</div>
              <div className="text-[11px] text-neutral-500">
                {unreadCount === 0 ? "All caught up" : `${unreadCount} unread`}
              </div>
            </div>
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] uppercase tracking-widest font-semibold text-neutral-600 hover:text-neutral-900"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            ) : null}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-6 text-center text-xs text-neutral-500">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-xs text-neutral-500 flex flex-col items-center gap-2">
                <CheckCircle size={32} weight="thin" className="text-neutral-300" />
                No notifications yet. You&apos;ll see task overdue alerts here.
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => openTask(n)}
                  className={`w-full text-left px-4 py-3 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 flex items-start gap-3 ${
                    n.read_at ? "opacity-70" : "bg-blue-50/30"
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${n.read_at ? "bg-neutral-200" : "bg-red-500"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-neutral-900 truncate">{n.title}</div>
                    <div className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-neutral-400 mt-1 uppercase tracking-widest font-semibold">{relTime(n.created_at)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
